import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Checkbox,
  ClickAwayListener,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  TextField,
} from '@mui/material';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';

export interface ColumnFilterProps {
  id: string;
  label: string;
  type: 'dropdown' | 'date-range' | 'text';
  options?: Array<{ id: string; label: string; value: string }>;
  onChange: (id: string, value: any) => void;
  multiple?: boolean;
  initialValue?: any;
}

export interface ColumnFilterRef {
  reset: () => void;
}

const toDateInputValue = (raw: unknown): string => {
  if (!raw) return '';
  if (raw instanceof Date) {
    const y = raw.getFullYear();
    const m = String(raw.getMonth() + 1).padStart(2, '0');
    const d = String(raw.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  if (typeof raw === 'string') return raw.slice(0, 10);
  return '';
};

export const ColumnFilter = forwardRef<ColumnFilterRef, ColumnFilterProps>(
  ({ id, label, type, options = [], onChange, multiple = false, initialValue }, ref) => {
    const [value, setValue] = useState<any>(() => {
      if (initialValue !== undefined && initialValue !== '' && initialValue !== null) {
        return initialValue;
      }
      return multiple ? [] : '';
    });
    const [startDate, setStartDate] = useState<string>(() =>
      toDateInputValue((initialValue as { start?: unknown } | undefined)?.start)
    );
    const [endDate, setEndDate] = useState<string>(() =>
      toDateInputValue((initialValue as { end?: unknown } | undefined)?.end)
    );
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [filteredOptions, setFilteredOptions] =
      useState<Array<{ id: string; label: string; value: string }>>(options);
    const [open, setOpen] = useState(false);
    const [searchFocused, setSearchFocused] = useState(false);

    // Reference to the select component and search input
    const selectRef = useRef<HTMLInputElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);

    // Store a stable reference to options to prevent infinite re-renders
    const optionsRef = useRef(options);

    // Update optionsRef only when the options array actually changes
    useEffect(() => {
      // Deep comparison to avoid unnecessary updates
      if (JSON.stringify(optionsRef.current) !== JSON.stringify(options)) {
        optionsRef.current = options;
        setFilteredOptions(options);
      }
    }, [options]);

    // Expose reset method via ref
    useImperativeHandle(ref, () => ({
      reset: () => {
        setValue(multiple ? [] : '');
        setStartDate('');
        setEndDate('');
        setSearchTerm('');
      },
    }));

    // Automatically focus the search input when dropdown opens
    useEffect(() => {
      if (open) {
        // Use a small timeout to ensure the dropdown is fully rendered
        const timer = setTimeout(() => {
          if (searchInputRef.current) {
            searchInputRef.current.focus();
            setSearchFocused(true);
          }
        }, 50);

        return () => clearTimeout(timer);
      }
    }, [open]);

    // Filter options when search term changes
    useEffect(() => {
      if (searchTerm.trim() === '') {
        setFilteredOptions(optionsRef.current);
        return;
      }

      const filtered = optionsRef.current.filter(option =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredOptions(filtered);
    }, [searchTerm]);

    const handleChange = (newValue: any) => {
      setValue(newValue);
      onChange(id, newValue);
    };

    const handleMultipleChange = (event: SelectChangeEvent<string[]>) => {
      const selectedValues = event.target.value as string[];
      setValue(selectedValues);

      // If we've unselected everything, explicitly send empty string
      // to indicate no filtering should occur
      if (selectedValues.length === 0) {
        onChange(id, '');
      } else {
        onChange(id, selectedValues);
      }
    };

    const handleDateRangeChange = (startValue: string, endValue: string) => {
      setStartDate(startValue);
      setEndDate(endValue);

      const dateFilter = {
        start: startValue ? new Date(startValue) : null,
        end: endValue ? new Date(endValue) : null,
      };

      // Only send the filter if at least one date is set
      if (startValue || endValue) {
        onChange(id, dateFilter);
      } else {
        onChange(id, '');
      }
    };

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(event.target.value);
    };

    const clearSearch = () => {
      setSearchTerm('');
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    };

    const handleOpen = () => {
      setOpen(true);
    };

    const handleClose = () => {
      setOpen(false);
      setSearchTerm(''); // Clear search when dropdown closes
      setSearchFocused(false);
    };

    const handleSearchFocus = () => {
      setSearchFocused(true);
    };

    // Handle keyboard events to prevent default Select behavior when search is focused
    const handleKeyDown = (event: React.KeyboardEvent) => {
      if (searchFocused) {
        // Prevent the select from hijacking keyboard events when search is focused
        event.stopPropagation();
      }
    };

    // Calculate maximum width based on options
    const getMaxWidth = () => {
      // Set a reasonable maximum width for the dropdown
      return '600px';
    };

    // Search input component for the dropdown
    const SearchInput = (
      <ClickAwayListener onClickAway={() => setSearchFocused(false)}>
        <TextField
          inputRef={searchInputRef}
          size="small"
          placeholder="Search..."
          value={searchTerm}
          onChange={handleSearchChange}
          onFocus={handleSearchFocus}
          onClick={e => {
            e.stopPropagation(); // Prevent dropdown from closing
            setSearchFocused(true);
          }}
          onKeyDown={handleKeyDown}
          fullWidth
          autoFocus // Auto focus when menu opens
          variant="outlined"
          sx={{ m: 1, width: 'calc(100% - 16px)' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: searchTerm ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={e => {
                    e.stopPropagation();
                    clearSearch();
                  }}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
      </ClickAwayListener>
    );

    // Common Menu Props to ensure consistent width and behavior
    const menuProps = {
      PaperProps: {
        sx: {
          maxHeight: 300,
          width: getMaxWidth(),
          // Ensure the width doesn't change regardless of content
          minWidth: getMaxWidth(),
          maxWidth: getMaxWidth(),
          // Optional: add ellipsis for overflowing text
          '& .MuiMenuItem-root': {
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          },
        },
      },
      // Don't auto focus any menu item when dropdown opens
      disableAutoFocusItem: true,
      // Use this to focus the search box immediately
      autoFocus: false,
    };

    switch (type) {
      case 'dropdown':
        if (multiple) {
          return (
            <FormControl fullWidth size="small" variant="outlined">
              <InputLabel id={`${id}-label`} shrink>
                {label}
              </InputLabel>
              <Select
                ref={selectRef}
                labelId={`${id}-label`}
                id={id}
                multiple
                value={value}
                onChange={handleMultipleChange}
                input={<OutlinedInput label={label} />}
                renderValue={selected => {
                  const selectedArray = selected as string[];
                  if (selectedArray.length === 0) {
                    return <em>All</em>;
                  }

                  return `${selectedArray.length} selected`;
                }}
                displayEmpty
                onOpen={handleOpen}
                onClose={handleClose}
                open={open}
                MenuProps={menuProps}
              >
                <Box component="li" sx={{ p: 0 }}>
                  {SearchInput}
                </Box>
                {filteredOptions.length === 0 ? (
                  <MenuItem disabled>No options found</MenuItem>
                ) : (
                  filteredOptions.map(option => (
                    <MenuItem key={option.id} value={option.value}>
                      <Checkbox checked={value.indexOf(option.value) > -1} />
                      <ListItemText primary={option.label} />
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          );
        } else {
          return (
            <FormControl fullWidth size="small" variant="outlined">
              <InputLabel id={`${id}-label`} shrink>
                {label}
              </InputLabel>
              <Select
                ref={selectRef}
                labelId={`${id}-label`}
                id={id}
                value={value}
                onChange={e => handleChange(e.target.value)}
                label={label}
                displayEmpty
                onOpen={handleOpen}
                onClose={handleClose}
                open={open}
                MenuProps={menuProps}
              >
                <Box component="li" sx={{ p: 0 }}>
                  {SearchInput}
                </Box>
                <MenuItem value="">
                  <em>All</em>
                </MenuItem>
                {filteredOptions.length === 0 ? (
                  <MenuItem disabled>No options found</MenuItem>
                ) : (
                  filteredOptions.map(option => (
                    <MenuItem key={option.id} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          );
        }

      case 'date-range':
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <TextField
              type="date"
              label={`${label} From`}
              value={startDate}
              onChange={e => handleDateRangeChange(e.target.value, endDate)}
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              type="date"
              label={`${label} To`}
              value={endDate}
              onChange={e => handleDateRangeChange(startDate, e.target.value)}
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        );

      case 'text':
        return (
          <TextField
            label={label}
            value={value}
            onChange={e => handleChange(e.target.value)}
            size="small"
            fullWidth
          />
        );

      default:
        return null;
    }
  }
);

ColumnFilter.displayName = 'ColumnFilter';

export default ColumnFilter;
